import {WboDelta,
    WboAnnotationData,
    WboAnnotationDocumentData, 
    WboMsg} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import WboDocument from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-document';
import WboPendingChangesQueue
    from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-pending-changes-queue';
import {WboBoardData} from '@/components/common/ed-board/ed-board-tools/ed-board-persistence/persistent-board-data';
import GenericQueue from '@/components/common/ed-board/ed-board-tools/wbo-client/util/generic-queue';
import _ from 'lodash';

/**
 * Defines the persistence operations for the WBO annotation document. Implementations will provide the underlying data
 * access logic.
 */
export default abstract class WboPersistenceProvider implements WboBoardData {
    /**
     * The WboDocument to persist.
     */
    private document: WboDocument;

    /**
     * The WboPendingOperationsQueue to persist.
     */
    private pendingQueue: WboPendingChangesQueue;

    /**
     * The WBO board name on which this persistence provider will work.
     */
    private readonly boardName: string;

    /**
     * Username of the board owner.
     */
    private readonly username: string;

    /**
     * A function wrapper that throttles the save operation to avoid saving too many times in a short period.
     */
    private readonly throttledSaveDoc: Function;

    /**
     * Like the throttledSaveDoc, but for the pending operations queue.
     */
    private readonly throttledSavePendingOps: Function;

    constructor(boardName: string, username: string) {
        this.document = new WboDocument();
        this.pendingQueue = new WboPendingChangesQueue(new GenericQueue<WboDelta>());
        this.boardName = boardName;
        this.username = username;

        this.throttledSaveDoc = _.throttle(() => 
            this.saveDataToStorage(this.docKey, this.document.getData()), 1000, {trailing: true});
        this.throttledSavePendingOps = _.throttle(() => 
            this.saveDataToStorage(this.pendingOpsKey, this.pendingQueue.queue().elements()), 1000, {trailing: true});
    }
   
    get docKey(): string {
        return `wbo_doc_${this.username}_${this.boardName}`;
    }

    get pendingOpsKey(): string {
        return `wbo_pending_${this.username}_${this.boardName}`;
    }

    /**
     * Gets the annotation data by its ID.
     */
    getAnnotationDataById_(annotationId: string) : WboAnnotationData | undefined {
        const pendingOps = this.pendingQueue.queue().elements();
        if (pendingOps) {
            for (let i = 0; i < pendingOps.length; i++) {
                let msg = pendingOps[i].msg as WboMsg;
                if (msg?.data?.id === annotationId) {
                    return msg.data;
                }
            }
        }
        const pending = this.document.getData()[annotationId];
        return pending;
    }

    /**
     * Gets the annotation data by its ID, first looking in the document, then in the pending ops if not found.
     */
    getAnnotationDataById(annotationId: string): WboAnnotationData | undefined {
        // First, look in the persisted document
        const docData = this.document.getData()[annotationId];
        if (docData) {
            return docData;
        }
        // If not found, look in the pending ops
        const pendingOps = this.pendingQueue.queue().elements();
        if (pendingOps) {
            for (let i = 0; i < pendingOps.length; i++) {
                let msg = pendingOps[i].msg as WboMsg;
                if (msg?.data?.id === annotationId) {
                    return msg.data;
                }
            }
        }

        // If not found, look in the inflight ops
        const inflightOpsQueue = this.pendingQueue.inflightOps().elements();
        if (inflightOpsQueue) {
            for (let i = 0; i < inflightOpsQueue.length; i++) {
                let msg = inflightOpsQueue[i].msg as WboMsg;
                if (msg?.data?.id === annotationId) {
                    return msg.data;
                }
            }
        }
        return undefined;
    }

    async readAnnotationsFile(): Promise<WboDocument> {
        const wboDocument = new WboDocument();
        const annotations: WboAnnotationDocumentData = await this.readDataFromStorage(this.docKey);
        if (annotations) {
            wboDocument.setData(annotations);
        }
        this.document = wboDocument;
        return this.document;
    }

    async readPendingChangesFile(): Promise<WboPendingChangesQueue> {
        const pendingOperationsQueue = new GenericQueue<WboDelta>();
        const ops: WboDelta[] = await this.readDataFromStorage(this.pendingOpsKey);
        if (ops) {
            pendingOperationsQueue.setQueue(ops);
        }
        this.pendingQueue = new WboPendingChangesQueue(new GenericQueue<WboDelta>(ops));
        return this.pendingQueue;
    }

    async createAnnotationsFile() {
        await this.saveAnnotationsFile();
    }

    async saveAnnotationsFile() {
        this.throttledSaveDoc();
    }

    async deleteAnnotationsFile() {
        await this.deleteFromStorage(this.docKey);
    }

    async createPendingChangesFile() {
        await this.savePendingChangesFile();
    }

    async appendToChangesFile(delta: WboDelta) {
        this.pendingQueue.enqueue(delta);
        await this.savePendingChangesFile();
    }

    async savePendingChangesFile() {
        this.throttledSavePendingOps();
    }

    async deletePendingChangesFile() {
        await this.deleteFromStorage(this.pendingOpsKey);
    }


    //Implementations classes will provide the underlying data access logic for this methods
   abstract saveDataToStorage(fileName : string, data : any): Promise<void>;
   abstract readDataFromStorage(fileName : string): Promise<any>;
   abstract deleteFromStorage(fileName : string): Promise<void>;

}